import { Check, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

// Falls back to the plain index-based completed/active/pending model when
// no stepStates map is supplied (e.g. other StepIndicator call sites, if
// any get added later, that don't track wizard invalidation state).
const getStepState = (step, index, currentStep, stepStates) => {
  if (stepStates && stepStates[step.id]) {
    const s = stepStates[step.id];
    // Map the 4-state wizard model onto the 4 visual states this component
    // renders: locked -> pending, current -> active, everything else as-is.
    if (s === 'locked') return 'pending';
    if (s === 'current') return 'active';
    return s; // 'completed' | 'stale'
  }
  if (index < currentStep) return 'completed';
  if (index === currentStep) return 'active';
  return 'pending';
};

// Style mappings
const STEP_STYLES = {
  completed: 'bg-green-500 text-white',
  stale: 'bg-amber-500 text-white',
  active: 'bg-[#0055FF] text-white',
  pending: 'bg-zinc-200 text-zinc-500'
};

const CONNECTOR_STYLES = {
  completed: 'bg-green-500',
  stale: 'bg-amber-500',
  pending: 'bg-zinc-200'
};

export const StepIndicator = ({ steps, currentStep, stepStates }) => {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-full py-2">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          {steps.map((step, index) => {
            const state = getStepState(step, index, currentStep, stepStates);
            const isCompleted = state === 'completed';
            const isStale = state === 'stale';
            const isActive = state === 'active';

            const circle = (
              <div
                className={`
                  w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs
                  transition-all duration-300 ${STEP_STYLES[state]}
                `}
                data-testid={`step-indicator-${step.id}`}
                data-step-state={state}
              >
                {isStale ? (
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
                ) : isCompleted ? (
                  <Check className="w-3.5 h-3.5" strokeWidth={2} />
                ) : (
                  index + 1
                )}
              </div>
            );

            return (
              <div key={step.id} className="flex items-center flex-1 last:flex-none">
                {/* Step Circle */}
                <div className="flex flex-col items-center">
                  {isStale ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{circle}</TooltipTrigger>
                      <TooltipContent>Needs regeneration — an earlier step changed</TooltipContent>
                    </Tooltip>
                  ) : circle}
                  <span className={`
                    mt-1 text-xs font-medium text-center hidden sm:block
                    ${isActive ? 'text-[#0055FF]' : isStale ? 'text-amber-600' : 'text-zinc-500'}
                  `}>
                    {step.label}
                  </span>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-2 h-0.5 hidden sm:block">
                    <div
                      className={`h-full transition-all duration-300 ${
                        CONNECTOR_STYLES[state] || CONNECTOR_STYLES.pending
                      }`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default StepIndicator;
